import { useState } from 'react';
import { Check, Layout, Loader2, AlertCircle } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { useTemplateDesigner } from '../TemplateDesignerContext';

// Template layout format for display
interface TemplateLayout {
  id: string;
  name: string;
  category: string;
  description: string;
  previewSvg?: string;
}

const CATEGORIES = [
  { id: 'all', name: 'All' },
  { id: 'modern', name: 'Modern' },
  { id: 'classic', name: 'Classic' },
  { id: 'minimal', name: 'Minimal' },
  { id: 'bold', name: 'Bold' },
];

// Mini preview component that shows actual template visual style
function TemplateMiniPreview({ templateId, isSelected }: { templateId: string; isSelected: boolean }) {
  const primary = isSelected ? '#6366f1' : '#94a3b8';
  const accent = isSelected ? 'rgba(99, 102, 241, 0.2)' : 'rgba(148, 163, 184, 0.15)';
  const line = isSelected ? 'rgba(99, 102, 241, 0.3)' : 'rgba(148, 163, 184, 0.2)';
  const bg = '#ffffff';
  const dark = isSelected ? '#1e1b4b' : '#374151';

  switch (templateId) {
    case 'classic-executive':
      // Serif style with centered header, ornamental divider
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Centered header */}
          <rect x="28" y="8" width="24" height="6" fill={primary} rx="1" />
          <rect x="32" y="16" width="16" height="2" fill={line} rx="0.5" />
          {/* Ornamental divider */}
          <line x1="15" y1="24" x2="65" y2="24" stroke={primary} strokeWidth="0.5" />
          <circle cx="40" cy="24" r="2" fill={primary} />
          <line x1="15" y1="24" x2="30" y2="24" stroke={primary} strokeWidth="1" />
          <line x1="50" y1="24" x2="65" y2="24" stroke={primary} strokeWidth="1" />
          {/* Two column address */}
          <rect x="8" y="30" width="25" height="2" fill={line} rx="0.5" />
          <rect x="8" y="34" width="20" height="1.5" fill={line} rx="0.5" />
          <rect x="47" y="30" width="25" height="2" fill={line} rx="0.5" />
          <rect x="52" y="34" width="20" height="1.5" fill={line} rx="0.5" />
          {/* Table with borders */}
          <rect x="8" y="45" width="64" height="6" fill={accent} stroke={primary} strokeWidth="0.3" />
          <rect x="8" y="51" width="64" height="5" fill="none" stroke={line} strokeWidth="0.3" />
          <rect x="8" y="56" width="64" height="5" fill="none" stroke={line} strokeWidth="0.3" />
          <rect x="8" y="61" width="64" height="5" fill="none" stroke={line} strokeWidth="0.3" />
          {/* Totals */}
          <rect x="48" y="72" width="24" height="3" fill={line} rx="0.5" />
          <rect x="48" y="77" width="24" height="4" fill={primary} rx="0.5" />
          {/* Footer note with centered text */}
          <rect x="20" y="88" width="40" height="2" fill={line} rx="0.5" />
        </svg>
      );

    case 'modern-minimal':
      // Ultra clean with lots of whitespace
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Logo placeholder */}
          <rect x="8" y="10" width="12" height="12" fill={primary} rx="2" />
          {/* Invoice title far right */}
          <rect x="50" y="10" width="22" height="5" fill={primary} rx="1" />
          <rect x="55" y="17" width="17" height="2" fill={line} rx="0.5" />
          {/* Minimal address block */}
          <rect x="8" y="32" width="30" height="2" fill={line} rx="0.5" />
          <rect x="8" y="36" width="24" height="1.5" fill={line} rx="0.5" />
          <rect x="8" y="40" width="20" height="1.5" fill={line} rx="0.5" />
          {/* Simple line table - no borders, just lines */}
          <line x1="8" y1="52" x2="72" y2="52" stroke={primary} strokeWidth="0.5" />
          <rect x="8" y="55" width="64" height="1.5" fill={line} rx="0.5" />
          <rect x="8" y="60" width="64" height="1.5" fill={line} rx="0.5" />
          <rect x="8" y="65" width="64" height="1.5" fill={line} rx="0.5" />
          <line x1="8" y1="70" x2="72" y2="70" stroke={line} strokeWidth="0.3" />
          {/* Right-aligned total only */}
          <rect x="52" y="76" width="20" height="5" fill={primary} rx="1" />
          {/* Minimal footer */}
          <line x1="8" y1="92" x2="72" y2="92" stroke={line} strokeWidth="0.3" />
        </svg>
      );

    case 'bold-stripe':
      // Side stripe accent design
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Bold left stripe */}
          <rect x="0" y="0" width="6" height="100" fill={primary} />
          {/* Header with large title */}
          <rect x="14" y="8" width="28" height="8" fill={primary} rx="1" />
          <rect x="14" y="18" width="18" height="2" fill={line} rx="0.5" />
          {/* Info badges */}
          <rect x="50" y="8" width="22" height="5" fill={accent} rx="1" stroke={primary} strokeWidth="0.3" />
          <rect x="50" y="15" width="22" height="5" fill={accent} rx="1" stroke={primary} strokeWidth="0.3" />
          {/* Address in card style */}
          <rect x="14" y="28" width="30" height="14" fill={accent} rx="2" />
          <rect x="17" y="31" width="20" height="2" fill={line} rx="0.5" />
          <rect x="17" y="35" width="16" height="1.5" fill={line} rx="0.5" />
          {/* Bold header row */}
          <rect x="14" y="48" width="58" height="7" fill={primary} rx="1" />
          <rect x="14" y="56" width="58" height="5" fill="none" stroke={line} strokeWidth="0.3" />
          <rect x="14" y="61" width="58" height="5" fill={accent} />
          <rect x="14" y="66" width="58" height="5" fill="none" stroke={line} strokeWidth="0.3" />
          {/* Total highlight */}
          <rect x="44" y="76" width="28" height="8" fill={primary} rx="1" />
          {/* Footer */}
          <rect x="14" y="90" width="40" height="2" fill={line} rx="0.5" />
        </svg>
      );

    case 'tech-grid':
      // Blueprint/technical style
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Grid background pattern */}
          <defs>
            <pattern id={`grid-${templateId}`} width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke={accent} strokeWidth="0.3" />
            </pattern>
          </defs>
          <rect x="4" y="4" width="72" height="92" fill={`url(#grid-${templateId})`} />
          {/* Corner markers */}
          <path d="M4 12 L4 4 L12 4" fill="none" stroke={primary} strokeWidth="0.8" />
          <path d="M68 4 L76 4 L76 12" fill="none" stroke={primary} strokeWidth="0.8" />
          <path d="M76 88 L76 96 L68 96" fill="none" stroke={primary} strokeWidth="0.8" />
          <path d="M12 96 L4 96 L4 88" fill="none" stroke={primary} strokeWidth="0.8" />
          {/* Monospace header */}
          <rect x="8" y="10" width="10" height="10" fill={primary} rx="1" />
          <rect x="22" y="10" width="24" height="4" fill={primary} rx="0.5" />
          <rect x="22" y="16" width="18" height="2" fill={line} rx="0.5" />
          <rect x="56" y="10" width="16" height="3" fill={line} rx="0.5" />
          <rect x="56" y="15" width="16" height="3" fill={line} rx="0.5" />
          {/* Data blocks */}
          <rect x="8" y="28" width="28" height="2" fill={line} rx="0.5" />
          <rect x="8" y="32" width="22" height="1.5" fill={line} rx="0.5" />
          <rect x="44" y="28" width="28" height="2" fill={line} rx="0.5" />
          <rect x="44" y="32" width="22" height="1.5" fill={line} rx="0.5" />
          {/* Table with grid lines */}
          <rect x="8" y="42" width="64" height="5" fill={accent} />
          <line x1="8" y1="42" x2="72" y2="42" stroke={primary} strokeWidth="0.5" />
          <line x1="8" y1="47" x2="72" y2="47" stroke={primary} strokeWidth="0.5" />
          <rect x="8" y="48" width="64" height="4" fill="none" />
          <line x1="8" y1="52" x2="72" y2="52" stroke={line} strokeWidth="0.3" />
          <rect x="8" y="53" width="64" height="4" fill="none" />
          <line x1="8" y1="57" x2="72" y2="57" stroke={line} strokeWidth="0.3" />
          <rect x="8" y="58" width="64" height="4" fill="none" />
          <line x1="8" y1="62" x2="72" y2="62" stroke={line} strokeWidth="0.3" />
          {/* Code-style totals */}
          <rect x="44" y="68" width="28" height="10" fill={accent} stroke={primary} strokeWidth="0.3" rx="1" />
          <rect x="47" y="71" width="12" height="2" fill={line} rx="0.5" />
          <rect x="47" y="74" width="18" height="2" fill={primary} rx="0.5" />
          {/* Footer ref number */}
          <rect x="8" y="88" width="30" height="2" fill={line} rx="0.5" />
        </svg>
      );

    case 'elegant-border':
      // Double border frame design
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Double border frame */}
          <rect x="4" y="4" width="72" height="92" fill="none" stroke={primary} strokeWidth="0.5" rx="1" />
          <rect x="7" y="7" width="66" height="86" fill="none" stroke={primary} strokeWidth="0.3" rx="0.5" />
          {/* Centered elegant header */}
          <rect x="28" y="12" width="24" height="5" fill={primary} rx="1" />
          <rect x="32" y="19" width="16" height="2" fill={line} rx="0.5" />
          {/* Decorative line */}
          <line x1="20" y1="26" x2="60" y2="26" stroke={primary} strokeWidth="0.3" />
          <circle cx="20" cy="26" r="1" fill={primary} />
          <circle cx="60" cy="26" r="1" fill={primary} />
          {/* Address blocks */}
          <rect x="12" y="32" width="24" height="2" fill={line} rx="0.5" />
          <rect x="12" y="36" width="18" height="1.5" fill={line} rx="0.5" />
          <rect x="48" y="32" width="20" height="2" fill={line} rx="0.5" />
          <rect x="48" y="36" width="16" height="1.5" fill={line} rx="0.5" />
          {/* Table with elegant styling */}
          <rect x="12" y="46" width="56" height="5" fill={accent} rx="0.5" />
          <rect x="12" y="52" width="56" height="4" fill="none" stroke={line} strokeWidth="0.2" />
          <rect x="12" y="56" width="56" height="4" fill="none" stroke={line} strokeWidth="0.2" />
          <rect x="12" y="60" width="56" height="4" fill="none" stroke={line} strokeWidth="0.2" />
          <line x1="12" y1="64" x2="68" y2="64" stroke={primary} strokeWidth="0.3" />
          {/* Totals */}
          <rect x="44" y="69" width="24" height="2" fill={line} rx="0.5" />
          <rect x="44" y="73" width="24" height="3" fill={primary} rx="0.5" />
          {/* Footer */}
          <rect x="24" y="84" width="32" height="2" fill={line} rx="0.5" />
        </svg>
      );

    case 'compact-pro':
      // Dense information layout with top and bottom bars
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Top bar */}
          <rect x="0" y="0" width="80" height="14" fill={primary} />
          <rect x="6" y="4" width="18" height="6" fill="rgba(255,255,255,0.2)" rx="1" />
          <rect x="56" y="5" width="18" height="4" fill="rgba(255,255,255,0.3)" rx="0.5" />
          {/* Dense info grid - 3 columns */}
          <rect x="4" y="18" width="23" height="2" fill={line} rx="0.5" />
          <rect x="4" y="22" width="18" height="1.5" fill={line} rx="0.5" />
          <rect x="4" y="25" width="20" height="1.5" fill={line} rx="0.5" />

          <rect x="29" y="18" width="23" height="2" fill={line} rx="0.5" />
          <rect x="29" y="22" width="18" height="1.5" fill={line} rx="0.5" />
          <rect x="29" y="25" width="15" height="1.5" fill={line} rx="0.5" />

          <rect x="54" y="18" width="22" height="2" fill={line} rx="0.5" />
          <rect x="54" y="22" width="16" height="1.5" fill={line} rx="0.5" />
          <rect x="54" y="25" width="18" height="1.5" fill={line} rx="0.5" />

          {/* Compact table */}
          <rect x="4" y="32" width="72" height="4" fill={primary} rx="0.5" />
          <rect x="4" y="37" width="72" height="3" fill="none" stroke={line} strokeWidth="0.2" />
          <rect x="4" y="40" width="72" height="3" fill={accent} />
          <rect x="4" y="43" width="72" height="3" fill="none" stroke={line} strokeWidth="0.2" />
          <rect x="4" y="46" width="72" height="3" fill={accent} />
          <rect x="4" y="49" width="72" height="3" fill="none" stroke={line} strokeWidth="0.2" />
          <rect x="4" y="52" width="72" height="3" fill={accent} />
          <rect x="4" y="55" width="72" height="3" fill="none" stroke={line} strokeWidth="0.2" />
          {/* Summary section */}
          <rect x="4" y="62" width="35" height="16" fill={accent} rx="1" />
          <rect x="7" y="65" width="20" height="2" fill={line} rx="0.5" />
          <rect x="7" y="69" width="25" height="1.5" fill={line} rx="0.5" />
          <rect x="7" y="73" width="18" height="1.5" fill={line} rx="0.5" />

          <rect x="42" y="62" width="34" height="16" fill={primary} rx="1" />
          <rect x="45" y="65" width="16" height="2" fill="rgba(255,255,255,0.3)" rx="0.5" />
          <rect x="45" y="69" width="22" height="2" fill="rgba(255,255,255,0.4)" rx="0.5" />
          <rect x="45" y="73" width="26" height="3" fill="rgba(255,255,255,0.5)" rx="0.5" />

          {/* Bottom bar */}
          <rect x="0" y="84" width="80" height="16" fill={accent} />
          <rect x="6" y="88" width="30" height="2" fill={line} rx="0.5" />
          <rect x="6" y="92" width="40" height="1.5" fill={line} rx="0.5" />
        </svg>
      );

    case 'split-modern':
      // Two-tone split design
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Left colored panel */}
          <rect x="0" y="0" width="26" height="100" fill={primary} />
          {/* Logo in left panel */}
          <rect x="6" y="10" width="14" height="14" fill="rgba(255,255,255,0.2)" rx="2" />
          {/* Company info in left panel */}
          <rect x="6" y="30" width="16" height="2" fill="rgba(255,255,255,0.4)" rx="0.5" />
          <rect x="6" y="34" width="12" height="1.5" fill="rgba(255,255,255,0.3)" rx="0.5" />
          <rect x="6" y="38" width="14" height="1.5" fill="rgba(255,255,255,0.3)" rx="0.5" />
          {/* Document info in left panel */}
          <rect x="6" y="50" width="16" height="3" fill="rgba(255,255,255,0.5)" rx="0.5" />
          <rect x="6" y="55" width="12" height="2" fill="rgba(255,255,255,0.3)" rx="0.5" />
          <rect x="6" y="59" width="14" height="2" fill="rgba(255,255,255,0.3)" rx="0.5" />
          {/* Right content area */}
          <rect x="32" y="10" width="28" height="6" fill={primary} rx="1" />
          <rect x="32" y="18" width="20" height="2" fill={line} rx="0.5" />
          {/* Bill to */}
          <rect x="32" y="28" width="40" height="12" fill={accent} rx="2" />
          <rect x="35" y="31" width="20" height="2" fill={line} rx="0.5" />
          <rect x="35" y="35" width="16" height="1.5" fill={line} rx="0.5" />
          {/* Table */}
          <rect x="32" y="46" width="40" height="5" fill={primary} rx="0.5" />
          <rect x="32" y="52" width="40" height="4" fill="none" stroke={line} strokeWidth="0.2" />
          <rect x="32" y="56" width="40" height="4" fill="none" stroke={line} strokeWidth="0.2" />
          <rect x="32" y="60" width="40" height="4" fill="none" stroke={line} strokeWidth="0.2" />
          {/* Totals */}
          <rect x="48" y="70" width="24" height="3" fill={line} rx="0.5" />
          <rect x="48" y="75" width="24" height="4" fill={primary} rx="0.5" />
          {/* Footer note */}
          <rect x="32" y="88" width="30" height="2" fill={line} rx="0.5" />
        </svg>
      );

    case 'rounded-soft':
      // Friendly design with rounded elements
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Top rounded header card */}
          <rect x="6" y="6" width="68" height="24" fill={accent} rx="6" />
          <rect x="12" y="10" width="14" height="14" fill={primary} rx="4" />
          <rect x="32" y="12" width="24" height="5" fill={primary} rx="2" />
          <rect x="32" y="19" width="18" height="2" fill={line} rx="1" />
          {/* Address card */}
          <rect x="6" y="36" width="32" height="16" fill={accent} rx="4" />
          <rect x="10" y="40" width="20" height="2" fill={line} rx="1" />
          <rect x="10" y="44" width="16" height="1.5" fill={line} rx="0.75" />
          <rect x="10" y="48" width="18" height="1.5" fill={line} rx="0.75" />
          {/* Date card */}
          <rect x="42" y="36" width="32" height="16" fill={accent} rx="4" />
          <rect x="46" y="40" width="18" height="2" fill={line} rx="1" />
          <rect x="46" y="44" width="14" height="1.5" fill={line} rx="0.75" />
          {/* Rounded table */}
          <rect x="6" y="58" width="68" height="6" fill={primary} rx="3" />
          <rect x="6" y="66" width="68" height="5" fill={accent} rx="2" />
          <rect x="6" y="72" width="68" height="5" fill="none" stroke={line} strokeWidth="0.3" rx="2" />
          <rect x="6" y="78" width="68" height="5" fill={accent} rx="2" />
          {/* Total pill */}
          <rect x="46" y="88" width="28" height="6" fill={primary} rx="3" />
        </svg>
      );

    case 'invoice-classic':
      // Traditional invoice format
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* INVOICE title top right, large */}
          <rect x="44" y="8" width="28" height="8" fill={primary} rx="0.5" />
          {/* Logo top left */}
          <rect x="8" y="8" width="16" height="10" fill={line} rx="1" />
          {/* Document details under title */}
          <rect x="44" y="18" width="28" height="2" fill={line} rx="0.5" />
          <rect x="44" y="22" width="28" height="2" fill={line} rx="0.5" />
          <rect x="44" y="26" width="28" height="2" fill={line} rx="0.5" />
          {/* From/To sections side by side */}
          <rect x="8" y="22" width="8" height="2" fill={primary} rx="0.5" />
          <rect x="8" y="26" width="26" height="2" fill={line} rx="0.5" />
          <rect x="8" y="30" width="22" height="1.5" fill={line} rx="0.5" />
          <rect x="8" y="34" width="18" height="1.5" fill={line} rx="0.5" />
          {/* Horizontal divider */}
          <line x1="8" y1="42" x2="72" y2="42" stroke={primary} strokeWidth="0.5" />
          {/* Traditional table with clear header */}
          <rect x="8" y="46" width="64" height="6" fill={primary} />
          <line x1="8" y1="53" x2="72" y2="53" stroke={line} strokeWidth="0.3" />
          <line x1="8" y1="59" x2="72" y2="59" stroke={line} strokeWidth="0.3" />
          <line x1="8" y1="65" x2="72" y2="65" stroke={line} strokeWidth="0.3" />
          <line x1="8" y1="71" x2="72" y2="71" stroke={primary} strokeWidth="0.5" />
          {/* Totals section */}
          <rect x="44" y="74" width="14" height="2" fill={line} rx="0.5" />
          <rect x="60" y="74" width="12" height="2" fill={line} rx="0.5" />
          <rect x="44" y="78" width="14" height="2" fill={line} rx="0.5" />
          <rect x="60" y="78" width="12" height="2" fill={line} rx="0.5" />
          <rect x="44" y="83" width="14" height="3" fill={primary} rx="0.5" />
          <rect x="60" y="83" width="12" height="3" fill={primary} rx="0.5" />
          {/* Footer */}
          <rect x="8" y="92" width="40" height="2" fill={line} rx="0.5" />
        </svg>
      );

    case 'dark-professional':
      // Dark header/footer design
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Dark header band */}
          <rect x="0" y="0" width="80" height="28" fill={dark} />
          <rect x="8" y="8" width="12" height="12" fill={primary} rx="2" />
          <rect x="26" y="10" width="26" height="5" fill="rgba(255,255,255,0.9)" rx="1" />
          <rect x="26" y="17" width="18" height="2" fill="rgba(255,255,255,0.4)" rx="0.5" />
          <rect x="56" y="10" width="16" height="3" fill="rgba(255,255,255,0.3)" rx="0.5" />
          <rect x="56" y="15" width="16" height="3" fill="rgba(255,255,255,0.3)" rx="0.5" />
          {/* Bill to section */}
          <rect x="8" y="34" width="30" height="2" fill={line} rx="0.5" />
          <rect x="8" y="38" width="24" height="1.5" fill={line} rx="0.5" />
          <rect x="8" y="42" width="20" height="1.5" fill={line} rx="0.5" />
          {/* Table */}
          <rect x="8" y="52" width="64" height="5" fill={dark} rx="0.5" />
          <rect x="8" y="58" width="64" height="4" fill="none" stroke={line} strokeWidth="0.2" />
          <rect x="8" y="62" width="64" height="4" fill={accent} />
          <rect x="8" y="66" width="64" height="4" fill="none" stroke={line} strokeWidth="0.2" />
          {/* Totals */}
          <rect x="48" y="74" width="24" height="3" fill={line} rx="0.5" />
          <rect x="48" y="79" width="24" height="4" fill={dark} rx="0.5" />
          {/* Dark footer */}
          <rect x="0" y="88" width="80" height="12" fill={dark} />
          <rect x="8" y="92" width="32" height="2" fill="rgba(255,255,255,0.4)" rx="0.5" />
        </svg>
      );

    case 'contractor-pro':
      // Contractor Pro - bold branding with info grid
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Header with logo and badge */}
          <rect x="8" y="8" width="14" height="14" fill={primary} rx="2" />
          <rect x="26" y="8" width="28" height="3" fill={line} rx="0.5" />
          <rect x="26" y="13" width="22" height="2" fill={line} rx="0.5" />
          <rect x="26" y="17" width="18" height="2" fill={line} rx="0.5" />
          <rect x="56" y="8" width="16" height="10" fill={primary} rx="1" />
          {/* Info grid */}
          <rect x="8" y="28" width="64" height="18" fill={accent} rx="2" />
          <rect x="11" y="31" width="18" height="2" fill={line} rx="0.5" />
          <rect x="11" y="35" width="14" height="1.5" fill={line} rx="0.5" />
          <rect x="11" y="39" width="16" height="1.5" fill={line} rx="0.5" />
          <rect x="32" y="31" width="18" height="2" fill={line} rx="0.5" />
          <rect x="32" y="35" width="14" height="1.5" fill={line} rx="0.5" />
          <rect x="53" y="31" width="16" height="2" fill={line} rx="0.5" />
          <rect x="53" y="35" width="12" height="1.5" fill={line} rx="0.5" />
          {/* Table */}
          <rect x="8" y="52" width="64" height="5" fill={primary} rx="0.5" />
          <rect x="8" y="58" width="64" height="4" fill="none" stroke={line} strokeWidth="0.3" />
          <rect x="8" y="62" width="64" height="4" fill="none" stroke={line} strokeWidth="0.3" />
          <rect x="8" y="66" width="64" height="4" fill="none" stroke={line} strokeWidth="0.3" />
          {/* Totals with highlight */}
          <rect x="44" y="76" width="28" height="4" fill={primary} rx="0.5" />
          <rect x="8" y="88" width="36" height="2" fill={line} rx="0.5" />
        </svg>
      );

    case 'construction-bold':
      // Construction Bold - dark header with accent stripe
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Dark header */}
          <rect x="0" y="0" width="80" height="22" fill={dark} />
          <rect x="8" y="6" width="12" height="10" fill={primary} rx="1" />
          <rect x="24" y="7" width="24" height="4" fill="rgba(255,255,255,0.8)" rx="0.5" />
          <rect x="24" y="13" width="16" height="2" fill="rgba(255,255,255,0.4)" rx="0.5" />
          <rect x="56" y="6" width="16" height="6" fill={primary} rx="1" />
          {/* Info cards */}
          <rect x="8" y="28" width="32" height="18" fill={accent} rx="2" />
          <rect x="8" y="28" width="3" height="18" fill={primary} />
          <rect x="14" y="31" width="20" height="3" fill={line} rx="0.5" />
          <rect x="14" y="36" width="16" height="2" fill={line} rx="0.5" />
          <rect x="14" y="40" width="18" height="2" fill={line} rx="0.5" />
          <rect x="44" y="28" width="28" height="18" fill={accent} rx="2" />
          <rect x="44" y="28" width="3" height="18" fill={primary} />
          <rect x="50" y="31" width="16" height="2" fill={line} rx="0.5" />
          <rect x="50" y="35" width="14" height="2" fill={line} rx="0.5" />
          {/* Table */}
          <rect x="8" y="52" width="64" height="5" fill={primary} rx="0.5" />
          <rect x="8" y="58" width="64" height="4" fill={accent} />
          <rect x="8" y="62" width="64" height="4" fill="none" stroke={line} strokeWidth="0.3" />
          <rect x="8" y="66" width="64" height="4" fill={accent} />
          {/* Totals */}
          <rect x="44" y="76" width="28" height="10" fill={primary} rx="1" />
          {/* Bottom stripe */}
          <rect x="0" y="96" width="80" height="4" fill={primary} />
        </svg>
      );

    case 'clean-service':
      // Clean Service - minimal with border emphasis
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Header with bottom border */}
          <rect x="8" y="10" width="12" height="12" fill={primary} rx="2" />
          <rect x="24" y="12" width="20" height="3" fill={line} rx="0.5" />
          <rect x="24" y="17" width="14" height="2" fill={line} rx="0.5" />
          <rect x="52" y="10" width="20" height="6" fill={primary} rx="1" />
          <rect x="52" y="18" width="16" height="2" fill={line} rx="0.5" />
          <line x1="8" y1="28" x2="72" y2="28" stroke={primary} strokeWidth="1" />
          {/* Two column info */}
          <rect x="8" y="34" width="20" height="2" fill={line} rx="0.5" />
          <rect x="8" y="38" width="26" height="2" fill={line} rx="0.5" />
          <rect x="8" y="42" width="22" height="2" fill={line} rx="0.5" />
          <rect x="52" y="34" width="20" height="2" fill={line} rx="0.5" />
          <rect x="56" y="38" width="16" height="2" fill={line} rx="0.5" />
          {/* Minimal table */}
          <line x1="8" y1="52" x2="72" y2="52" stroke={primary} strokeWidth="0.5" />
          <rect x="8" y="55" width="64" height="2" fill={line} rx="0.5" />
          <rect x="8" y="60" width="64" height="2" fill={line} rx="0.5" />
          <rect x="8" y="65" width="64" height="2" fill={line} rx="0.5" />
          <line x1="8" y1="70" x2="72" y2="70" stroke={primary} strokeWidth="0.5" />
          {/* Clean totals */}
          <rect x="52" y="76" width="20" height="3" fill={line} rx="0.5" />
          <line x1="52" y1="82" x2="72" y2="82" stroke={primary} strokeWidth="1" />
          <rect x="52" y="84" width="20" height="4" fill={primary} rx="0.5" />
          {/* Footer */}
          <rect x="8" y="94" width="36" height="2" fill={line} rx="0.5" />
        </svg>
      );

    case 'premium-modern':
      // Premium Modern - gradient header with cards
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          {/* Gradient header */}
          <defs>
            <linearGradient id={`gradient-${templateId}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={primary} />
              <stop offset="100%" stopColor={isSelected ? '#a855f7' : '#64748b'} />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="80" height="24" fill={`url(#gradient-${templateId})`} />
          <rect x="8" y="6" width="12" height="12" fill="rgba(255,255,255,0.2)" rx="2" />
          <rect x="24" y="7" width="22" height="4" fill="rgba(255,255,255,0.9)" rx="0.5" />
          <rect x="24" y="13" width="16" height="2" fill="rgba(255,255,255,0.5)" rx="0.5" />
          <rect x="52" y="7" width="20" height="5" fill="rgba(255,255,255,0.9)" rx="0.5" />
          <rect x="56" y="14" width="16" height="2" fill="rgba(255,255,255,0.5)" rx="0.5" />
          {/* Info cards */}
          <rect x="8" y="30" width="20" height="18" fill="none" stroke={line} strokeWidth="0.5" rx="2" />
          <rect x="11" y="33" width="14" height="2" fill={line} rx="0.5" />
          <rect x="11" y="37" width="10" height="1.5" fill={line} rx="0.5" />
          <rect x="11" y="41" width="12" height="1.5" fill={line} rx="0.5" />
          <rect x="30" y="30" width="20" height="18" fill="none" stroke={line} strokeWidth="0.5" rx="2" />
          <rect x="33" y="33" width="14" height="2" fill={line} rx="0.5" />
          <rect x="33" y="37" width="10" height="1.5" fill={line} rx="0.5" />
          <rect x="52" y="30" width="20" height="18" fill="none" stroke={line} strokeWidth="0.5" rx="2" />
          <rect x="55" y="33" width="14" height="2" fill={line} rx="0.5" />
          <rect x="55" y="38" width="12" height="4" fill={accent} rx="0.5" />
          {/* Table */}
          <rect x="8" y="54" width="64" height="5" fill={primary} rx="0.5" />
          <rect x="8" y="60" width="64" height="4" fill="none" stroke={line} strokeWidth="0.3" />
          <rect x="8" y="64" width="64" height="4" fill="none" stroke={line} strokeWidth="0.3" />
          {/* Gradient totals */}
          <rect x="44" y="74" width="28" height="14" fill="none" stroke={line} strokeWidth="0.5" rx="2" />
          <rect x="44" y="74" width="28" height="5" fill={accent} rx="2" />
          <rect x="44" y="83" width="28" height="5" fill={`url(#gradient-${templateId})`} rx="2" />
        </svg>
      );

    default:
      return (
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect width="80" height="100" fill={bg} />
          <rect x="8" y="8" width="20" height="8" fill={primary} rx="1" />
          <rect x="8" y="24" width="64" height="4" fill={accent} />
          <rect x="8" y="32" width="64" height="3" fill={line} />
          <rect x="8" y="38" width="64" height="3" fill={line} />
        </svg>
      );
  }
}

export function TemplatePanel() {
  const { settings, updateSettings, templates, templatesLoaded, templatesError } = useTemplateDesigner();
  const selectedLayout = settings.selectedLayout;
  const [activeCategory, setActiveCategory] = useState('all');

  // Transform context templates to local layout format for display
  const layouts: TemplateLayout[] = templates.map(t => ({
    id: t.slug,
    name: t.name,
    category: t.category,
    description: t.description,
    previewSvg: t.previewSvg,
  }));

  const filteredLayouts = activeCategory === 'all'
    ? layouts
    : layouts.filter(layout => layout.category === activeCategory);

  // Loading state
  if (!templatesLoaded) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading templates...</p>
      </div>
    );
  }

  // Error state
  if (templatesError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <AlertCircle className="w-8 h-8 text-destructive" />
        <p className="text-sm text-destructive">{templatesError}</p>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Category Filter */}
      <div className="flex gap-2 flex-wrap">
        {CATEGORIES.map((category) => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`
              px-3 py-1.5 text-xs font-medium rounded-full border transition-colors
              ${activeCategory === category.id
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border hover:bg-accent hover:border-primary/30'
              }
            `}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Template Grid */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold flex items-center gap-2">
          <Layout className="w-4 h-4 text-primary" />
          Choose Layout ({layouts.length} templates)
        </Label>

        {filteredLayouts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No templates found in this category.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredLayouts.map((layout) => {
              const isSelected = selectedLayout === layout.id;

              return (
                <button
                  key={layout.id}
                  onClick={() => updateSettings('selectedLayout', layout.id)}
                  className={`
                    relative group text-left rounded-xl border-2 transition-all duration-200 overflow-hidden
                    ${isSelected
                      ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10'
                      : 'border-border hover:border-primary/30 hover:bg-accent/50'
                    }
                  `}
                >
                  {/* Checkmark */}
                  {isSelected && (
                    <div className="absolute top-2 right-2 z-10 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md">
                      <Check className="w-3 h-3" />
                    </div>
                  )}

                  {/* Mini Preview - use API preview_svg or fallback */}
                  <div className="aspect-[4/5] p-2 bg-gradient-to-br from-muted/30 to-muted/10">
                    <div className="w-full h-full rounded-md overflow-hidden shadow-sm border border-border/50 bg-white flex items-center justify-center">
                      {layout.previewSvg ? (
                        (() => {
                          const primary = settings.colorTheme.primary;
                          return (
                            <div
                              className="w-full h-full [&>svg]:w-full [&>svg]:h-full"
                              style={{
                                ['--theme-primary' as string]: primary,
                                ['--theme-secondary' as string]: `${primary}10`,      // 6% opacity
                                ['--theme-accent' as string]: `${primary}1a`,         // 10% opacity
                                ['--theme-table-border' as string]: `${primary}30`,   // 19% opacity
                                ['--theme-dark' as string]: primary,
                              }}
                              dangerouslySetInnerHTML={{ __html: layout.previewSvg }}
                            />
                          );
                        })()
                      ) : (
                        <TemplateMiniPreview templateId={layout.id} isSelected={isSelected} />
                      )}
                    </div>
                  </div>

                  {/* Layout Info - fixed height for consistency */}
                  <div className="p-3 bg-background h-16 flex flex-col justify-center">
                    <span className={`block text-sm font-semibold truncate ${isSelected ? 'text-primary' : ''}`}>
                      {layout.name}
                    </span>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {layout.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
